import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  ArrowLeft, Trash2, Plus, ImagePlus, Eye,
  Copy, ArrowUp, ArrowDown, Move,
} from "lucide-react";
import { toast } from "sonner";
import { useAppDialog } from "../components/AppDialog";
import AdBanner from "../components/AdBanner";
import AdminShell from "../components/admin/AdminShell";

const BASE = process.env.REACT_APP_BACKEND_URL;
const SLOTS = [
  { id: "home_hero", label: "Home · hero (above booth)" },
  { id: "home_secondary", label: "Home · secondary (below booth)" },
  { id: "games_row", label: "Games · between rows" },
  { id: "games_row_bottom", label: "Games · bottom of grid" },
  { id: "cart_footer", label: "Cart · below checkout" },
  { id: "notifications", label: "Notification center" },
];
const EMPTY_FORM = {
  slot: "home_hero",
  headline: "",
  cta_text: "",
  image_url: "",
  dest_url: "",
  active: true,
  priority: 0,
  start_at: "",
  end_at: "",
};

// Compress + convert any user-selected image to a data URL under 1MB so the
// backend cap accepts it. Uses a canvas so PNGs / huge photos are safely
// resized without a build-time image library.
const imageToDataUrl = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const img = new Image();
      img.onload = () => {
        const maxW = 900;
        const scale = Math.min(1, maxW / img.width);
        const w = Math.round(img.width * scale);
        const h = Math.round(img.height * scale);
        const c = document.createElement("canvas");
        c.width = w;
        c.height = h;
        c.getContext("2d").drawImage(img, 0, 0, w, h);
        resolve(c.toDataURL("image/webp", 0.82));
      };
      img.onerror = reject;
      img.src = reader.result;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

const AdminAds = () => {
  const nav = useNavigate();
  const { confirm } = useAppDialog();
  const [rows, setRows] = useState([]);
  const [stats, setStats] = useState({ per_ad: [], per_slot: [] });
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(EMPTY_FORM);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [previewSlot, setPreviewSlot] = useState(null);

  const refresh = () => {
    setLoading(true);
    Promise.all([
      fetch(`${BASE}/api/admin/ads`, { credentials: "include" }).then((r) => {
        if (r.status === 401) {
          nav("/admin", { replace: true });
          return null;
        }
        return r.ok ? r.json() : null;
      }),
      fetch(`${BASE}/api/admin/ads/stats?days=30`, { credentials: "include" })
        .then((r) => (r.ok ? r.json() : null))
        .catch(() => null),
    ])
      .then(([listJ, statsJ]) => {
        setRows(listJ?.rows || []);
        setStats(statsJ || { per_ad: [], per_slot: [] });
      })
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  };
  useEffect(refresh, [nav]);

  // Map ad_id -> {impressions, clicks, ctr, reach} for quick row lookup.
  const statsByAd = useMemo(() => {
    const m = {};
    for (const r of stats.per_ad || []) m[r.ad_id] = r;
    return m;
  }, [stats]);

  const onPickImage = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    try {
      const url = await imageToDataUrl(f);
      setForm((s) => ({ ...s, image_url: url }));
    } catch {
      toast("Couldn't read image — try another file", { duration: 2200 });
    }
  };

  const startEdit = (row) => {
    setEditingId(row.ad_id);
    setForm({
      slot: row.slot,
      headline: row.headline || "",
      cta_text: row.cta_text || "",
      image_url: row.image_url || "",
      dest_url: row.dest_url || "",
      active: !!row.active,
      priority: Number.isFinite(row.priority) ? row.priority : 0,
      start_at: row.start_at || "",
      end_at: row.end_at || "",
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const resetForm = () => {
    setEditingId(null);
    setForm(EMPTY_FORM);
  };

  const save = async () => {
    if (!form.headline.trim()) {
      toast("Headline is required", { duration: 1800 });
      return;
    }
    if (!form.image_url) {
      toast("Please upload a banner image", { duration: 1800 });
      return;
    }
    setSaving(true);
    try {
      const url = editingId
        ? `${BASE}/api/admin/ads/${encodeURIComponent(editingId)}`
        : `${BASE}/api/admin/ads`;
      const method = editingId ? "PATCH" : "POST";
      const r = await fetch(url, {
        method,
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(form),
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) {
        toast(j.detail || "Save failed", { duration: 2600 });
        return;
      }
      toast(editingId ? "Ad updated" : "Ad created", { duration: 1500 });
      resetForm();
      refresh();
    } finally {
      setSaving(false);
    }
  };

  const remove = async (ad) => {
    const ok = await confirm({
      title: "Delete this ad?",
      message: `"${ad.headline}" will stop showing in the ${ad.slot} slot immediately.`,
      tone: "warning",
      confirmLabel: "Delete",
    });
    if (!ok) return;
    const r = await fetch(`${BASE}/api/admin/ads/${encodeURIComponent(ad.ad_id)}`, {
      method: "DELETE",
      credentials: "include",
    });
    if (r.ok) {
      toast("Deleted", { duration: 1400 });
      if (editingId === ad.ad_id) resetForm();
      refresh();
    } else {
      toast("Delete failed", { duration: 2000 });
    }
  };

  // Clone an ad into the same slot. Server appends "(copy)" and starts the
  // clone paused so admin can edit safely before flipping active on.
  const duplicate = async (ad) => {
    const r = await fetch(
      `${BASE}/api/admin/ads/${encodeURIComponent(ad.ad_id)}/duplicate`,
      { method: "POST", credentials: "include" },
    );
    const j = await r.json().catch(() => ({}));
    if (r.ok) {
      toast("Duplicated · paused so you can edit safely", { duration: 2200 });
      refresh();
      // Auto-scroll the admin to the editor with the new copy loaded so a
      // rename before publishing feels natural.
      if (j.ad) startEdit(j.ad);
    } else {
      toast(j.detail || "Duplicate failed", { duration: 2000 });
    }
  };

  // Move an ad to a different slot without opening the editor.
  const moveToSlot = async (ad, targetSlot) => {
    if (targetSlot === ad.slot) return;
    const r = await fetch(
      `${BASE}/api/admin/ads/${encodeURIComponent(ad.ad_id)}/move`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ slot: targetSlot }),
      },
    );
    if (r.ok) {
      const label = SLOTS.find((s) => s.id === targetSlot)?.label || targetSlot;
      toast(`Moved to ${label}`, { duration: 1600 });
      refresh();
    } else {
      const j = await r.json().catch(() => ({}));
      toast(j.detail || "Move failed", { duration: 2000 });
    }
  };

  // Nudge an ad up or down among its slot-mates by swapping priority.
  const reorder = async (ad, direction) => {
    const r = await fetch(
      `${BASE}/api/admin/ads/${encodeURIComponent(ad.ad_id)}/reorder`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ direction }),
      },
    );
    if (r.ok) {
      refresh();
    } else {
      toast("Reorder failed", { duration: 1800 });
    }
  };

  const grouped = useMemo(() => {
    const g = Object.fromEntries(SLOTS.map((s) => [s.id, []]));
    for (const r of rows) (g[r.slot] || (g[r.slot] = [])).push(r);
    // Sort each slot bucket by priority DESC then updated_at DESC so the
    // admin sees the row order that will actually be served to kids.
    for (const k of Object.keys(g)) {
      g[k].sort((a, b) => {
        const pa = a.priority ?? 0;
        const pb = b.priority ?? 0;
        if (pa !== pb) return pb - pa;
        return (b.updated_at || "").localeCompare(a.updated_at || "");
      });
    }
    return g;
  }, [rows]);

  return (
    <AdminShell>
    <div className="dots-bg pb-16" data-testid="admin-ads-page">
      <div className="max-w-5xl mx-auto p-4 sm:p-6">
        <h1 className="font-display text-3xl sm:text-4xl mb-1">
          🎯 Banner ads
        </h1>
        <p className="text-sm mb-4" style={{ color: "#484848" }}>
          Kid-safe self-hosted promos. No third-party trackers, no external
          scripts, images served from 67Cafe. Destination URLs must be
          internal (/shop, /games, …) unless the host is on the allowlist.
        </p>

        {/* Per-slot performance roll-up (last 30 days). Ties in priority
            get A/B-rotated per visit, so impressions and clicks here
            reflect real airtime, not just newest-wins. */}
        <section
          className="chunky-card p-4 mb-6"
          data-testid="admin-ads-slot-stats"
          style={{ background: "#fff" }}
        >
          <h2 className="font-display text-lg mb-3">
            📊 Slot performance · last 30 days
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2">
            {SLOTS.map((s) => {
              const row = (stats.per_slot || []).find((r) => r.slot === s.id);
              const imp = row?.impressions || 0;
              const clk = row?.clicks || 0;
              const ctr = imp ? Math.round((clk / imp) * 100) : 0;
              return (
                <div
                  key={s.id}
                  className="chunky-card p-3"
                  data-testid={`slot-stats-${s.id}`}
                  style={{ background: "#FFF9E6" }}
                >
                  <div className="font-display text-sm truncate" title={s.label}>
                    {s.label}
                  </div>
                  <div className="mt-1 flex flex-wrap gap-1 text-xs">
                    <span
                      className="chunky-badge"
                      style={{ background: "#EAF6FF", color: "#2b2b2b", fontSize: 10, padding: "2px 6px" }}
                    >
                      👀 {imp.toLocaleString()}
                    </span>
                    <span
                      className="chunky-badge"
                      style={{ background: "#FFF3D6", color: "#2b2b2b", fontSize: 10, padding: "2px 6px" }}
                    >
                      👆 {clk.toLocaleString()}
                    </span>
                    <span
                      className="chunky-badge"
                      style={{ background: "#E9FFE6", color: "#2b2b2b", fontSize: 10, padding: "2px 6px" }}
                    >
                      CTR {ctr}%
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </section>

        {/* Editor */}
        <section
          className="chunky-card p-4 sm:p-5 mb-6"
          data-testid="admin-ads-editor"
          style={{ background: "#fff" }}
        >
          <div className="flex items-center gap-2 mb-3">
            {editingId ? <Eye size={20} strokeWidth={3} /> : <Plus size={20} strokeWidth={3} />}
            <h2 className="font-display text-xl">
              {editingId ? "Edit ad" : "New ad"}
            </h2>
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <label className="flex flex-col gap-1 text-sm">
              <span className="font-display" style={{ color: "#484848" }}>Slot</span>
              <select
                data-testid="ad-form-slot"
                className="chunky-input"
                value={form.slot}
                onChange={(e) => setForm((s) => ({ ...s, slot: e.target.value }))}
              >
                {SLOTS.map((s) => (
                  <option key={s.id} value={s.id}>{s.label}</option>
                ))}
              </select>
            </label>
            <label className="flex flex-col gap-1 text-sm">
              <span className="font-display" style={{ color: "#484848" }}>Active</span>
              <button
                type="button"
                data-testid="ad-form-active"
                onClick={() => setForm((s) => ({ ...s, active: !s.active }))}
                className="btn-3d btn-sm"
                style={{ background: form.active ? "#54D62C" : "#eee" }}
              >
                {form.active ? "🟢 On" : "⚪ Off"}
              </button>
            </label>
            <label className="flex flex-col gap-1 text-sm">
              <span className="font-display" style={{ color: "#484848" }}>
                Priority
              </span>
              <input
                type="number"
                data-testid="ad-form-priority"
                className="chunky-input"
                value={form.priority}
                onChange={(e) =>
                  setForm((s) => ({ ...s, priority: Number(e.target.value) || 0 }))
                }
              />
              <span className="text-xs" style={{ color: "#8a8a8a" }}>
                Higher wins when multiple active ads share a slot.
              </span>
            </label>
            <label className="flex flex-col gap-1 text-sm sm:col-span-2">
              <span className="font-display" style={{ color: "#484848" }}>Headline (≤80 chars)</span>
              <input
                data-testid="ad-form-headline"
                className="chunky-input"
                value={form.headline}
                maxLength={80}
                onChange={(e) => setForm((s) => ({ ...s, headline: e.target.value }))}
                placeholder="Explore the new plushie corner!"
              />
            </label>
            <label className="flex flex-col gap-1 text-sm">
              <span className="font-display" style={{ color: "#484848" }}>CTA text (≤24)</span>
              <input
                data-testid="ad-form-cta"
                className="chunky-input"
                value={form.cta_text}
                maxLength={24}
                onChange={(e) => setForm((s) => ({ ...s, cta_text: e.target.value }))}
                placeholder="Shop plushies"
              />
            </label>
            <label className="flex flex-col gap-1 text-sm">
              <span className="font-display" style={{ color: "#484848" }}>Destination URL</span>
              <input
                data-testid="ad-form-dest"
                className="chunky-input"
                value={form.dest_url}
                onChange={(e) => setForm((s) => ({ ...s, dest_url: e.target.value }))}
                placeholder="/shop/plushies"
              />
              <span className="text-xs" style={{ color: "#8a8a8a" }}>
                Internal only: leave empty, or use <code>/shop</code>,{" "}
                <code>/games</code>, <code>/orders</code>,{" "}
                <code>/stickers</code>, <code>/leaderboard</code>,{" "}
                <code>/profile</code>, <code>/wishlist</code>. Sub-paths
                without a matching page will send kids back to home.
              </span>
            </label>
            <label className="flex flex-col gap-1 text-sm">
              <span className="font-display" style={{ color: "#484848" }}>Start (optional ISO)</span>
              <input
                data-testid="ad-form-start"
                className="chunky-input"
                value={form.start_at}
                onChange={(e) => setForm((s) => ({ ...s, start_at: e.target.value }))}
                placeholder="2026-03-01T00:00:00+00:00"
              />
            </label>
            <label className="flex flex-col gap-1 text-sm">
              <span className="font-display" style={{ color: "#484848" }}>End (optional ISO)</span>
              <input
                data-testid="ad-form-end"
                className="chunky-input"
                value={form.end_at}
                onChange={(e) => setForm((s) => ({ ...s, end_at: e.target.value }))}
                placeholder="2026-04-01T00:00:00+00:00"
              />
            </label>
            <label className="flex flex-col gap-1 text-sm sm:col-span-2">
              <span className="font-display flex items-center gap-2" style={{ color: "#484848" }}>
                <ImagePlus size={16} /> Banner image (auto-compressed to ~900px WebP)
              </span>
              <input
                type="file"
                accept="image/*"
                data-testid="ad-form-image"
                onChange={onPickImage}
              />
              {form.image_url ? (
                <img
                  src={form.image_url}
                  alt="preview"
                  data-testid="ad-form-image-preview"
                  style={{
                    marginTop: 8, borderRadius: 12,
                    border: "3px solid #2b2b2b", maxHeight: 160,
                  }}
                />
              ) : null}
            </label>
          </div>
          <div className="mt-4 flex flex-wrap gap-2">
            <button
              type="button"
              data-testid="ad-form-save"
              className="btn-3d btn-orange"
              onClick={save}
              disabled={saving}
            >
              {editingId ? "Save changes" : "Create ad"}
            </button>
            {editingId ? (
              <button
                type="button"
                data-testid="ad-form-cancel"
                className="btn-3d btn-ghost"
                onClick={resetForm}
              >
                Cancel edit
              </button>
            ) : null}
          </div>
        </section>

        {/* Live preview */}
        {previewSlot ? (
          <section
            className="chunky-card p-4 mb-6"
            style={{ background: "#EAF6FF" }}
            data-testid="admin-ads-preview"
          >
            <div className="flex items-center justify-between mb-2">
              <div className="font-display">
                Live preview — <code>{previewSlot}</code>
              </div>
              <button
                type="button"
                className="btn-3d btn-sm btn-ghost"
                onClick={() => setPreviewSlot(null)}
                data-testid="admin-ads-preview-close"
              >
                Close
              </button>
            </div>
            <AdBanner slot={previewSlot} />
          </section>
        ) : null}

        {/* Per-slot list */}
        {loading ? (
          <p className="text-sm" style={{ color: "#484848" }}>Loading…</p>
        ) : (
          SLOTS.map((s) => (
            <section
              key={s.id}
              className="chunky-card p-4 mb-4 overflow-hidden"
              data-testid={`admin-ads-slot-${s.id}`}
            >
              <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                <h3 className="font-display text-lg">
                  {s.label}
                  <span
                    className="ml-2 chunky-badge"
                    style={{ background: "#FFB400", color: "#2b2b2b", fontSize: 12 }}
                  >
                    {grouped[s.id]?.length || 0}
                  </span>
                </h3>
                <button
                  type="button"
                  className="btn-3d btn-sm btn-purple"
                  onClick={() => setPreviewSlot(s.id)}
                  data-testid={`admin-ads-preview-${s.id}`}
                >
                  <Eye size={14} strokeWidth={3} /> Live preview
                </button>
              </div>
              {(grouped[s.id] || []).length === 0 ? (
                <p className="text-sm" style={{ color: "#8a8a8a" }}>
                  No ads in this slot yet.
                </p>
              ) : (
                <ul className="grid gap-2 xl:grid-cols-2">
                  {grouped[s.id].map((r, idx) => (
                    <li
                      key={r.ad_id}
                      className="chunky-card p-3 flex flex-col gap-3 sm:flex-row sm:items-start"
                      data-testid={`ad-row-${r.ad_id}`}
                      style={{ background: r.active ? "#fff" : "#f5f5f5", overflow: "hidden" }}
                    >
                      {/* Top row on mobile: image + main info side-by-side.
                          On sm+ we keep the whole thing on one line. */}
                      <div className="flex items-start gap-3 flex-1 min-w-0">
                        <img
                          src={r.image_url}
                          alt={r.headline}
                          style={{
                            width: 56, height: 56, borderRadius: 10,
                            border: "2px solid #2b2b2b", objectFit: "cover",
                            flexShrink: 0,
                          }}
                        />
                      <div className="flex-1 min-w-0">
                        <div
                          className="font-display truncate flex items-center gap-2"
                          data-testid={`ad-row-headline-${r.ad_id}`}
                        >
                          {r.headline}
                          <span
                            className="chunky-badge shrink-0"
                            style={{
                              background: "#FFF3D6",
                              color: "#2b2b2b",
                              fontSize: 10,
                              padding: "2px 6px",
                            }}
                            data-testid={`ad-row-priority-${r.ad_id}`}
                            title="Priority — higher wins when multiple ads share a slot"
                          >
                            ⭐ {r.priority ?? 0}
                          </span>
                        </div>
                        <div className="text-xs" style={{ color: "#484848" }}>
                          {r.cta_text ? `${r.cta_text} → ` : ""}
                          <code>{r.dest_url || "(none)"}</code>
                        </div>
                        <div className="text-xs mt-0.5" style={{ color: "#8a8a8a" }}>
                          {r.active ? "🟢 active" : "⚪ paused"} ·{" "}
                          {r.updated_at?.slice(0, 10) || "—"}
                        </div>
                        {/* Impression + click + CTR chip (30-day window). */}
                        {(() => {
                          const s = statsByAd[r.ad_id];
                          const imp = s?.impressions || 0;
                          const clk = s?.clicks || 0;
                          const ctr = imp ? Math.round((clk / imp) * 100) : 0;
                          const reach = s?.reach || 0;
                          return (
                            <div
                              className="text-xs mt-1 flex flex-wrap gap-1"
                              data-testid={`ad-row-stats-${r.ad_id}`}
                            >
                              <span
                                className="chunky-badge"
                                style={{ background: "#EAF6FF", color: "#2b2b2b", fontSize: 10, padding: "2px 6px" }}
                                title="Impressions (30d)"
                              >
                                👀 {imp}
                              </span>
                              <span
                                className="chunky-badge"
                                style={{ background: "#EFE7FF", color: "#2b2b2b", fontSize: 10, padding: "2px 6px" }}
                                title="Unique visitors (reach, 30d)"
                              >
                                🧍 {reach}
                              </span>
                              <span
                                className="chunky-badge"
                                style={{ background: "#FFF3D6", color: "#2b2b2b", fontSize: 10, padding: "2px 6px" }}
                                title="Clicks (30d)"
                              >
                                👆 {clk}
                              </span>
                              <span
                                className="chunky-badge"
                                style={{ background: "#E9FFE6", color: "#2b2b2b", fontSize: 10, padding: "2px 6px" }}
                                title="Click-through rate (30d)"
                              >
                                CTR {ctr}%
                              </span>
                            </div>
                          );
                        })()}
                        {/* Inline "Move to slot" picker — never leaves the page. */}
                        <div className="mt-2 flex items-center gap-2 flex-wrap">
                          <Move size={14} strokeWidth={3} aria-hidden="true" />
                          <select
                            data-testid={`ad-row-move-${r.ad_id}`}
                            className="chunky-input"
                            style={{ padding: "4px 8px", fontSize: 12, height: 30 }}
                            value={r.slot}
                            onChange={(e) => moveToSlot(r, e.target.value)}
                            aria-label="Move to slot"
                          >
                            {SLOTS.map((opt) => (
                              <option key={opt.id} value={opt.id}>
                                {opt.label}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>
                      </div>
                      {/* Action bar. On mobile it becomes a wrapping row of
                          equal-height buttons under the card body so nothing
                          spills out of the section container. On sm+ it
                          returns to the compact right-column stack. */}
                      <div
                        className="flex flex-wrap gap-1 sm:flex-col sm:shrink-0"
                        data-testid={`ad-row-actions-${r.ad_id}`}
                      >
                        <div className="flex gap-1">
                          <button
                            type="button"
                            data-testid={`ad-row-up-${r.ad_id}`}
                            className="btn-3d btn-sm"
                            aria-label="Move up"
                            disabled={idx === 0}
                            style={{ opacity: idx === 0 ? 0.5 : 1, padding: "4px 8px" }}
                            onClick={() => reorder(r, "up")}
                          >
                            <ArrowUp size={14} strokeWidth={3} />
                          </button>
                          <button
                            type="button"
                            data-testid={`ad-row-down-${r.ad_id}`}
                            className="btn-3d btn-sm"
                            aria-label="Move down"
                            disabled={idx === grouped[s.id].length - 1}
                            style={{
                              opacity: idx === grouped[s.id].length - 1 ? 0.5 : 1,
                              padding: "4px 8px",
                            }}
                            onClick={() => reorder(r, "down")}
                          >
                            <ArrowDown size={14} strokeWidth={3} />
                          </button>
                        </div>
                        <button
                          type="button"
                          data-testid={`ad-row-edit-${r.ad_id}`}
                          className="btn-3d btn-sm"
                          onClick={() => startEdit(r)}
                        >
                          Edit
                        </button>
                        <button
                          type="button"
                          data-testid={`ad-row-duplicate-${r.ad_id}`}
                          className="btn-3d btn-sm"
                          aria-label="Duplicate"
                          style={{ background: "#EAF6FF" }}
                          onClick={() => duplicate(r)}
                        >
                          <Copy size={14} strokeWidth={3} />
                        </button>
                        <button
                          type="button"
                          data-testid={`ad-row-delete-${r.ad_id}`}
                          className="btn-3d btn-sm"
                          aria-label="Delete"
                          style={{ background: "#FFECEC" }}
                          onClick={() => remove(r)}
                        >
                          <Trash2 size={14} strokeWidth={3} />
                        </button>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          ))
        )}
      </div>
    </div>
    </AdminShell>
  );
};

export default AdminAds;
